/**
 * Timeline lanes (maintainer request after the Sprint 3 preview, Concept §4.2 "grouped in lanes"): with one row per
 * mission the timeline cannot hold dozens of missions. Missions that do not overlap in time share a lane — the
 * label beside a bar counts as part of it — and the lanes can be grouped by agency or by destination. Pure.
 */

export type LaneGrouping = 'none' | 'agency' | 'destination';
export const LANE_GROUPINGS: readonly LaneGrouping[] = ['none', 'agency', 'destination'];

export function isLaneGrouping(value: unknown): value is LaneGrouping {
  return typeof value === 'string' && (LANE_GROUPINGS as readonly string[]).includes(value);
}

export interface LaneItem {
  id: string;
  /** Extent on the time axis (ms): from launch to the end of the bar, plus the room its label needs. */
  start: number;
  end: number;
  /** Group key (agency id, destination); '' without grouping. */
  group: string;
}

export interface LaneGroup {
  key: string;
  /** Mission ids per lane, in order of their start. */
  lanes: string[][];
}

/**
 * Packs the items into as few lanes as possible per group (greedy by start time, which is optimal for intervals):
 * an item goes into the first lane whose last item ends at least `gap` before it starts. Groups keep the order of
 * `groupOrder` (unknown keys at the end, alphabetically).
 */
export function packLanes(items: readonly LaneItem[], gap: number, groupOrder: readonly string[] = []): LaneGroup[] {
  const byGroup = new Map<string, LaneItem[]>();
  for (const it of items) {
    let list = byGroup.get(it.group);
    if (!list) byGroup.set(it.group, (list = []));
    list.push(it);
  }
  const rank = (k: string): number => {
    const i = groupOrder.indexOf(k);
    return i < 0 ? groupOrder.length : i;
  };
  const keys = [...byGroup.keys()].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  return keys.map((key) => {
    const sorted = [...byGroup.get(key)!].sort(
      (a, b) => a.start - b.start || a.end - b.end || a.id.localeCompare(b.id),
    );
    const lanes: string[][] = [];
    const ends: number[] = [];
    for (const it of sorted) {
      let lane = ends.findIndex((end) => end + gap <= it.start);
      if (lane < 0) {
        lane = lanes.length;
        lanes.push([]);
        ends.push(-Infinity);
      }
      lanes[lane]!.push(it.id);
      ends[lane] = it.end;
    }
    return { key, lanes };
  });
}

/** Destinations, from near to far; a mission belongs to its farthest target's. */
export const DESTINATIONS = ['earth-orbit', 'moon', 'inner', 'small-bodies', 'outer', 'beyond'] as const;
export type Destination = (typeof DESTINATIONS)[number];

const DESTINATION_OF: Record<string, Destination> = {
  earth: 'earth-orbit',
  moon: 'moon',
  mercury: 'inner',
  venus: 'inner',
  mars: 'inner',
  sun: 'inner',
  jupiter: 'outer',
  saturn: 'outer',
  uranus: 'outer',
  neptune: 'outer',
  pluto: 'beyond',
};

/**
 * Destination of a mission from its target ids. Moons count as their planet (`parentOf`), Kuiper-belt objects and
 * unknown far bodies as "beyond", other small bodies as "small-bodies".
 */
export function destinationOf(
  targets: readonly string[],
  parentOf: (id: string) => string | null = () => null,
  kindOf: (id: string) => string | null = () => null,
): Destination {
  let best = 0;
  for (const target of targets) {
    const parent = parentOf(target);
    const kind = kindOf(target);
    const d: Destination =
      DESTINATION_OF[target] ??
      (parent && parent !== 'sun' ? (DESTINATION_OF[parent] ?? 'small-bodies') : undefined) ??
      (kind === 'kbo' || kind === 'dwarf-planet' ? 'beyond' : 'small-bodies');
    best = Math.max(best, DESTINATIONS.indexOf(d));
  }
  return DESTINATIONS[best]!;
}
